if (!customElements.get('ct-couple-product')) {
  customElements.define('ct-couple-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.data = JSON.parse(this.querySelector('[data-pair-data]').textContent);
      this.strings = this.data.strings;
      this.events = new AbortController();
      this.busy = false;
      this.slide = 0;
      this.colors = Object.fromEntries(['his', 'her'].map(wearer => [
        wearer, this.querySelector(`[data-color-wearer="${wearer}"] input:checked`).value
      ]));
      this.sizes = { his: '', her: '' };
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type=submit]');
      this.hero = this.querySelector('.ct-pair__hero');
      this.stage = this.querySelector('.ct-pair__stage');
      this.labels = this.data.historyLover === 'him'
        ? [this.strings.pair, this.strings.his, this.strings.her]
        : [this.strings.pair, this.strings.her, this.strings.his];
      const options = { signal: this.events.signal };
      this.addEventListener('change', e => {
        const colorWearer = e.target.closest('[data-color-wearer]')?.dataset.colorWearer;
        if (colorWearer) {
          this.colors[colorWearer] = e.target.value;
          this.slide = this.colors.his === this.colors.her || colorWearer === 'his' ? 0 : 1;
          this.updateGallery();
        }
        const wearer = e.target.closest('[data-wearer]')?.dataset.wearer;
        if (wearer) this.sizes[wearer] = e.target.value;
        this.querySelector('[data-error]').hidden = true;
        this.updateSelection();
      }, options);
      this.addEventListener('click', e => {
        const arrow = e.target.closest('[data-direction]');
        const thumb = e.target.closest('[data-slide]');
        if (arrow) this.showSlide(this.slide + Number(arrow.dataset.direction));
        if (thumb) this.showSlide(Number(thumb.dataset.slide));
      }, options);
      this.stage.addEventListener('keydown', e => {
        if (e.target !== this.stage) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.showSlide(this.slide + (e.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      this.hero.draggable = false;
      this.stage.addEventListener('pointerdown', e => {
        if (e.isPrimary === false || e.button !== 0 || e.target.closest('button')) return;
        this.swipeStart = { x: e.clientX, y: e.clientY, id: e.pointerId };
        this.stage.setPointerCapture(e.pointerId);
      }, options);
      this.stage.addEventListener('pointerup', e => {
        if (!this.swipeStart || this.swipeStart.id !== e.pointerId) return;
        const dx = e.clientX - this.swipeStart.x;
        const dy = e.clientY - this.swipeStart.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.showSlide(this.slide + (dx < 0 ? 1 : -1));
        }
        this.swipeStart = null;
      }, options);
      this.stage.addEventListener('pointercancel', () => { this.swipeStart = null; }, options);
      this.stage.addEventListener('lostpointercapture', () => { this.swipeStart = null; }, options);
      this.form.addEventListener('submit', e => this.addPair(e), options);
      this.updateSelection();
      this.updateGallery();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.initialized = false;
    }
    productFor(wearer) {
      const isPrimary = (wearer === 'his') === (this.data.historyLover === 'him');
      return this.data[isPrimary ? 'primary' : 'companion'];
    }
    variantFor(wearer, size) {
      const p = this.productFor(wearer);
      const ci = p.options.findIndex(o => o.toLowerCase() === 'color');
      const si = p.options.findIndex(o => o.toLowerCase() === 'size');
      return p.variants.find(v => v.options[ci] === this.colors[wearer] && v.options[si] === size);
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: this.data.currency
      }).format(cents / 100);
    }
    updateSelection() {
      const selected = {};
      const estimates = {};
      for (const wearer of ['his', 'her']) {
        const radios = this.querySelectorAll(`[data-wearer="${wearer}"] input`);
        for (const radio of radios) {
          const variant = this.variantFor(wearer, radio.value);
          radio.disabled = !variant || !variant.available || this.busy;
          if ((!variant || !variant.available) && radio.checked) {
            radio.checked = false;
            this.sizes[wearer] = '';
          }
        }
        selected[wearer] = this.sizes[wearer] ? this.variantFor(wearer, this.sizes[wearer]) : null;
        const p = this.productFor(wearer);
        const ci = p.options.findIndex(o => o.toLowerCase() === 'color');
        estimates[wearer] = selected[wearer] || p.variants
          .filter(v => v.options[ci] === this.colors[wearer] && v.available)
          .sort((a, b) => a.price - b.price)[0];
      }
      this.selected = selected;
      const hasStock = Boolean(estimates.his && estimates.her);
      const total = hasStock ? estimates.his.price + estimates.her.price : 0;
      const compare = hasStock ? (estimates.his.compare_at_price || 0) + (estimates.her.compare_at_price || 0) : 0;
      this.querySelector('[data-pair-price]').textContent = hasStock ? this.money(total) : this.strings.unavailable;
      const compareElement = this.querySelector('[data-pair-compare]');
      compareElement.hidden = !this.data.showCompare || !hasStock ||
        !estimates.his.compare_at_price || !estimates.her.compare_at_price || compare <= total;
      compareElement.textContent = this.money(compare);
      const ready = selected.his?.available && selected.her?.available;
      this.button.disabled = this.busy || !hasStock;
      this.querySelector('[data-button-label]').textContent = this.busy
        ? this.strings.adding : !hasStock ? this.strings.unavailable : this.strings.add;
      this.querySelector('[data-selection]').textContent = ready
        ? `${this.strings.his}: ${this.colors.his}, ${this.sizes.his} · ${this.strings.her}: ${this.colors.her}, ${this.sizes.her}`
        : '';
      for (const wearer of ['his', 'her']) {
        this.querySelector(`[data-color-label="${wearer}"]`).textContent = this.colors[wearer];
      }
      const fabric = this.querySelector('[data-fabric-summary]');
      if (fabric) {
        const hisHeather = this.colors.his.startsWith('Heather');
        const herHeather = this.colors.her.startsWith('Heather');
        fabric.textContent = hisHeather !== herHeather ? this.strings.fabricVaries :
          hisHeather ? this.strings.fabricHeather : this.strings.fabricCotton;
      }
      this.querySelectorAll('.ct-pair__colors input').forEach(input => { input.disabled = this.busy; });
    }
    showSlide(index) {
      const count = this.galleryItems().length;
      this.slide = (index + count) % count;
      this.updateGallery();
    }
    imageUrl(source, width) {
      const url = new URL(source, location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    }
    galleryItems() {
      if (this.colors.his === this.colors.her) {
        const gallery = this.data.galleries.find(g => g.color === this.colors.his);
        return gallery.images.map((src, index) => ({
          src, label: this.labels[index], color: gallery.color
        }));
      }
      return ['his', 'her'].map(wearer => {
        const gallery = this.data.galleries.find(g => g.color === this.colors[wearer]);
        const primary = (wearer === 'his') === (this.data.historyLover === 'him');
        return { src: gallery.images[primary ? 1 : 2], label: this.strings[wearer], color: this.colors[wearer] };
      });
    }
    updateGallery() {
      const items = this.galleryItems();
      if (this.slide >= items.length) this.slide = 0;
      const item = items[this.slide];
      const src = item.src;
      this.hero.srcset = [400, 600, 800, 1000, 1400].map(w => `${this.imageUrl(src, w)} ${w}w`).join(', ');
      this.hero.src = this.imageUrl(src, 1400);
      this.hero.alt = `${this.data.heading} — ${item.color} — ${item.label}`;
      this.querySelector('[data-slide-count]').textContent = `${this.slide + 1} / ${items.length}`;
      this.querySelectorAll('[data-slide]').forEach((button, index) => {
        button.hidden = index >= items.length;
        if (button.hidden) return;
        button.setAttribute('aria-pressed', String(index === this.slide));
        button.setAttribute('aria-label', `${items[index].color} — ${items[index].label}`);
        const image = button.querySelector('img');
        image.srcset = `${this.imageUrl(items[index].src, 100)} 100w, ${this.imageUrl(items[index].src, 200)} 200w`;
        image.src = this.imageUrl(items[index].src, 200);
        image.alt = `${items[index].color} — ${items[index].label}`;
      });
    }
    error(message) {
      const node = this.querySelector('[data-error]');
      node.textContent = message;
      node.hidden = false;
    }
    async addPair(event) {
      event.preventDefault();
      if (this.busy) return;
      if (!this.selected.his?.available || !this.selected.her?.available) {
        this.form.reportValidity();
        return;
      }
      const variants = { his: this.selected.his, her: this.selected.her };
      this.busy = true;
      this.querySelector('[data-error]').hidden = true;
      this.updateSelection();
      const cart = document.querySelector('cart-drawer');
      const routeRoot = window.Shopify?.routes?.root || '/';
      const sectionIds = cart?.getSectionsToRender().map(s => s.id) || ['cart-icon-bubble'];
      const pairId = `${this.data.handle}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const items = ['his', 'her'].map(wearer => ({
        id: variants[wearer].id,
        quantity: 1,
        properties: {
          'Matching pair': this.data.heading,
          'T-shirt': this.strings[wearer],
          '_ct_pair': pairId
        }
      }));
      cart?.setActiveElement(this.button);
      let added = false;
      try {
        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sectionIds, sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          this.error(result.description || result.message || this.strings.error);
          return;
        }
        added = true;
        if (!cart) {
          location.assign(`${routeRoot}cart`);
          return;
        }
        let sections = result.sections;
        if (!sections || sectionIds.some(id => !sections[id])) {
          const refresh = await fetch(`${routeRoot}?sections=${sectionIds.map(encodeURIComponent).join(',')}`);
          sections = await refresh.json();
        }
        if (sectionIds.some(id => !sections[id])) throw new Error('Missing cart sections');
        cart.classList.remove('is-empty');
        cart.renderContents({ ...result, sections });
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ct-couple-product', cartData: result })).catch(() => {});
        }
        this.querySelector('[data-selection]').textContent = this.strings.added;
      } catch (error) {
        if (added) {
          location.assign(`${routeRoot}cart`);
        } else {
          this.error(this.strings.uncertain);
        }
      } finally {
        this.busy = false;
        this.updateSelection();
      }
    }
  });
}
